<template>
  <div class="yard-space-board">
    <div class="filter-panel">
      <el-form :inline="true" :model="query" size="small" label-width="68px">
        <el-form-item label="煤棚">
          <el-select v-model="query.storeNo" filterable clearable placeholder="全部煤棚" @change="handleStoreChange">
            <el-option v-for="item in storeOptions" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </el-form-item>
        <el-form-item label="库区">
          <el-select v-model="query.areaId" filterable clearable placeholder="全部库区" @change="handleAreaChange">
            <el-option v-for="item in areaOptions" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </el-form-item>
        <el-form-item label="堆位">
          <el-select v-model="query.locationCode" filterable clearable placeholder="全部堆位">
            <el-option v-for="item in locationOptions" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </el-form-item>
        <el-form-item label="煤种">
          <el-select v-model="query.varNo" filterable clearable placeholder="全部煤种">
            <el-option v-for="item in varietyOptions" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </el-form-item>
        <el-form-item label="客户">
          <el-select v-model="query.custNo" filterable clearable placeholder="全部客户">
            <el-option v-for="item in customerOptions" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </el-form-item>
        <el-form-item label="库存状态">
          <el-select v-model="query.stockStatus" clearable placeholder="全部状态">
            <el-option label="空仓" :value="0" />
            <el-option label="正常" :value="1" />
            <el-option label="高位" :value="2" />
          </el-select>
        </el-form-item>
        <el-form-item class="filter-actions">
          <el-button type="primary" icon="el-icon-search" @click="fetchBoard">查询</el-button>
          <el-button icon="el-icon-refresh-left" @click="resetQuery">重置</el-button>
          <el-button icon="el-icon-refresh" :loading="loading" @click="fetchBoard">刷新</el-button>
        </el-form-item>
      </el-form>
    </div>

    <div class="metric-grid">
      <div v-for="item in metrics" :key="item.label" class="metric-card" :class="item.className">
        <span>{{ item.label }}</span>
        <strong>{{ item.value }}</strong>
      </div>
    </div>

    <el-alert
      v-if="errorMessage"
      class="board-alert"
      :title="errorMessage"
      type="error"
      show-icon
      :closable="false"
    />
    <el-alert
      v-if="capacityMissingCount"
      class="board-alert"
      :title="`存在 ${capacityMissingCount} 个堆位未配置容量，占比按 0 展示`"
      type="warning"
      show-icon
      :closable="false"
    />

    <div v-loading="loading" class="board-layout">
      <div class="yard-panel">
        <div class="panel-title">
          <span>平面堆位看板</span>
          <em>按当前查询结果库存吨数归一化高度</em>
        </div>
        <el-empty v-if="!flatStacks.length && !loading" description="暂无堆位数据" />
        <div v-else class="store-list">
          <div v-for="store in normalizedStores" :key="store.storeKey" class="store-block">
            <div class="store-title">
              <strong>{{ store.storeName || store.storeNo || "未命名煤棚" }}</strong>
              <span>{{ store.stackCount }} 个堆位 / {{ formatNumber(store.stockAmount) }} 吨</span>
            </div>
            <div v-for="area in store.areas" :key="area.areaKey" class="area-block">
              <div class="area-title">
                <span>{{ area.areaName || "默认库区" }}</span>
                <em>{{ area.stacks.length }} 个堆位</em>
              </div>
              <div class="yard-map" :class="{ 'yard-map--free': !area.hasLayout }">
                <button
                  v-for="stack in area.stacks"
                  :key="stack.stackKey"
                  class="stack-cell"
                  :class="[statusClass(stack), { 'stack-cell--active': selectedStack && selectedStack.stackKey === stack.stackKey }]"
                  :style="stackListStyle(stack)"
                  type="button"
                  @click="selectStack(stack)"
                >
                  <span class="stack-cell__fill" :style="{ height: stackVisualHeight(stack) + '%' }"></span>
                  <span class="stack-cell__content">
                    <strong>{{ stack.locationName || stack.locationCode || "未命名堆位" }}</strong>
                    <span class="stack-cell__meta">
                      <em>{{ displayVarName(stack.varName) }}</em>
                      <small>{{ formatNumber(stack.stockAmount) }} 吨 / {{ toPercent(stack.capacityRate) }}%</small>
                    </span>
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="side-panel">
        <div class="panel-section">
          <div class="panel-title">
            <span>煤种汇总</span>
            <em>{{ varietyList.length }} 类</em>
          </div>
          <el-empty v-if="!varietyList.length" description="暂无煤种数据" />
          <div v-else class="variety-list">
            <div v-for="item in varietyList" :key="item.varKey" class="variety-item">
              <div class="variety-item__head">
                <strong>{{ item.varName }}</strong>
                <span>{{ formatNumber(item.stockAmount) }} 吨</span>
              </div>
              <el-progress :percentage="toPercent(item.capacityRate)" :show-text="false" :stroke-width="8" />
              <div class="variety-item__foot">
                <span>占比 {{ toPercent(item.capacityRate) }}%</span>
                <em>{{ item.locationText || "-" }}</em>
              </div>
            </div>
          </div>
        </div>

        <div class="panel-section detail-section">
          <div class="panel-title">
            <span>堆位详情</span>
            <em>{{ selectedStack ? selectedStack.stockStatusText || statusText(selectedStack.stockStatus) : "未选择" }}</em>
          </div>
          <el-empty v-if="!selectedStack" description="请选择堆位" />
          <el-descriptions v-else :column="1" size="small" border>
            <el-descriptions-item label="煤棚">{{ selectedStack.storeName || "-" }}</el-descriptions-item>
            <el-descriptions-item label="库区">{{ selectedStack.areaName || "-" }}</el-descriptions-item>
            <el-descriptions-item label="堆位">{{ selectedStack.locationName || selectedStack.locationCode || "-" }}</el-descriptions-item>
            <el-descriptions-item label="煤种">{{ selectedStack.varName || "-" }}</el-descriptions-item>
            <el-descriptions-item label="库存">{{ formatNumber(selectedStack.stockAmount) }} 吨</el-descriptions-item>
            <el-descriptions-item label="容量">{{ formatNumber(selectedStack.capacityAmount) }} 吨</el-descriptions-item>
            <el-descriptions-item label="占比">{{ toPercent(selectedStack.capacityRate) }}%</el-descriptions-item>
            <el-descriptions-item label="客户">{{ selectedStack.custName || selectedStack.custNo || "-" }}</el-descriptions-item>
            <el-descriptions-item label="合同">{{ selectedStack.contractExtId || "-" }}</el-descriptions-item>
            <el-descriptions-item label="更新时间">{{ selectedStack.updateTime || "-" }}</el-descriptions-item>
          </el-descriptions>
        </div>
      </div>
    </div>

    <div class="table-panel">
      <div class="panel-title">
        <span>堆位明细</span>
        <em>{{ flatStacks.length }} 条</em>
      </div>
      <el-table :data="flatStacks" size="small" border stripe height="320">
        <el-table-column prop="storeName" label="煤棚" min-width="130" show-overflow-tooltip />
        <el-table-column prop="areaName" label="库区" min-width="120" show-overflow-tooltip />
        <el-table-column prop="locationName" label="堆位" min-width="130" show-overflow-tooltip />
        <el-table-column prop="varName" label="煤种" min-width="120" show-overflow-tooltip />
        <el-table-column prop="stockAmount" label="当前存量(吨)" min-width="120" align="right">
          <template slot-scope="{ row }">{{ formatNumber(row.stockAmount) }}</template>
        </el-table-column>
        <el-table-column prop="capacityAmount" label="堆位容量(吨)" min-width="120" align="right">
          <template slot-scope="{ row }">{{ formatNumber(row.capacityAmount) }}</template>
        </el-table-column>
        <el-table-column prop="capacityRate" label="占比" min-width="150">
          <template slot-scope="{ row }">
            <div class="capacity-cell">
              <el-progress :percentage="toPercent(row.capacityRate)" :show-text="false" :stroke-width="8" />
              <span>{{ toPercent(row.capacityRate) }}%</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="custName" label="客户" min-width="140" show-overflow-tooltip />
        <el-table-column prop="contractExtId" label="合同" min-width="120" show-overflow-tooltip />
        <el-table-column prop="stockStatusText" label="状态" min-width="90">
          <template slot-scope="{ row }">
            <el-tag :type="statusType(row.stockStatus)" size="small">{{ row.stockStatusText || statusText(row.stockStatus) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="updateTime" label="更新时间" min-width="160" show-overflow-tooltip />
      </el-table>
    </div>
  </div>
</template>

<script>
import { getYardSpaceBoard } from "./api";

export default {
  name: "YardSpaceBoard",
  data() {
    return {
      loading: false,
      errorMessage: "",
      query: {
        storeNo: "",
        areaId: "",
        locationCode: "",
        varNo: "",
        custNo: "",
        stockStatus: "",
      },
      boardData: {
        summary: {},
        varieties: [],
        stores: [],
      },
      selectedStack: null,
    };
  },
  computed: {
    normalizedStores() {
      const sourceStores = this.boardData.stores || [];
      if (sourceStores.length) {
        return sourceStores.map((store, storeIndex) => {
          const rawAreas = store.areas && store.areas.length
            ? store.areas
            : [{ areaId: store.areaId, areaName: store.areaName, stacks: store.stacks || [] }];
          const areas = rawAreas.map((area, areaIndex) => this.normalizeArea(area, store, areaIndex));
          const stacks = areas.reduce((list, area) => list.concat(area.stacks), []);
          return {
            ...store,
            storeKey: store.storeNo || `store-${storeIndex}`,
            areas,
            stackCount: stacks.length,
            stockAmount: stacks.reduce((sum, item) => sum + this.toNumber(item.stockAmount), 0),
          };
        });
      }
      return this.groupStacksToStores(this.flatStacksFromList);
    },
    flatStacks() {
      return this.normalizedStores.reduce((storeList, store) => {
        const stacks = (store.areas || []).reduce((areaList, area) => areaList.concat(area.stacks || []), []);
        return storeList.concat(stacks);
      }, []);
    },
    flatStacksFromList() {
      const list = this.boardData.stacks || this.boardData.records || this.boardData.list || [];
      return list.map((item, index) => this.normalizeStack(item, {}, {}, index));
    },
    maxStockAmount() {
      const max = Math.max.apply(null, this.flatStacks.map((item) => this.toNumber(item.stockAmount)));
      return max > 0 && Number.isFinite(max) ? max : 1;
    },
    summary() {
      const summary = this.boardData.summary || {};
      if (Object.keys(summary).length) return summary;
      const totalStockAmount = this.flatStacks.reduce((sum, item) => sum + this.toNumber(item.stockAmount), 0);
      const totalCapacityAmount = this.flatStacks.reduce((sum, item) => sum + this.toNumber(item.capacityAmount), 0);
      const warningCount = this.flatStacks.filter((item) => this.toNumber(item.capacityRate) >= 90 || item.stockStatus === 2).length;
      const emptyCount = this.flatStacks.filter((item) => this.toNumber(item.stockAmount) <= 0 || item.stockStatus === 0).length;
      const latestUpdateTime = this.flatStacks
        .map((item) => item.updateTime)
        .filter(Boolean)
        .sort()
        .pop();
      return {
        totalStockAmount,
        totalCapacityAmount,
        totalAvailableAmount: Math.max(totalCapacityAmount - totalStockAmount, 0),
        stackCount: this.flatStacks.length,
        warningCount,
        emptyCount,
        latestUpdateTime,
      };
    },
    metrics() {
      return [
        { label: "库存总量", value: `${this.formatNumber(this.summary.totalStockAmount)} 吨`, className: "metric-card--primary" },
        { label: "总容量", value: `${this.formatNumber(this.summary.totalCapacityAmount)} 吨`, className: "metric-card--success" },
        { label: "可用容量", value: `${this.formatNumber(this.summary.totalAvailableAmount)} 吨`, className: "metric-card--warning" },
        { label: "堆位数量", value: this.summary.stackCount || 0, className: "" },
        { label: "高位堆位", value: this.summary.warningCount || this.summary.highCount || 0, className: "metric-card--danger" },
        { label: "空仓堆位", value: this.summary.emptyCount || 0, className: "" },
        { label: "最新更新时间", value: this.summary.updateTime || this.summary.latestUpdateTime || "-", className: "metric-card--time" },
      ];
    },
    varietyList() {
      const source = this.boardData.varieties || [];
      if (source.length) {
        return source
          .filter((item) => this.hasMaintainedVariety(item))
          .map((item, index) => ({
            ...item,
            varKey: item.varNo || item.varName || `var-${index}`,
            stockAmount: this.toNumber(item.stockAmount),
            capacityAmount: this.toNumber(item.capacityAmount),
            capacityRate: this.resolveRate(item.stockAmount, item.capacityAmount, item.capacityRate),
            locationText: item.locationText || this.formatLocations(item.locations || item.locationNames || []),
          }));
      }
      const group = {};
      this.flatStacks.forEach((stack) => {
        if (!this.hasMaintainedVariety(stack)) return;
        const key = stack.varNo || stack.varName || "unknown";
        if (!group[key]) {
          group[key] = {
            varKey: key,
            varNo: stack.varNo,
            varName: stack.varName,
            stockAmount: 0,
            capacityAmount: 0,
            locations: [],
          };
        }
        group[key].stockAmount += this.toNumber(stack.stockAmount);
        group[key].capacityAmount += this.toNumber(stack.capacityAmount);
        group[key].locations.push(stack.locationName || stack.locationCode);
      });
      return Object.keys(group).map((key) => ({
        ...group[key],
        capacityRate: this.resolveRate(group[key].stockAmount, group[key].capacityAmount),
        locationText: this.formatLocations(group[key].locations),
      }));
    },
    storeOptions() {
      const map = {};
      this.flatStacks.forEach((item) => {
        if (item.storeNo) map[item.storeNo] = item.storeName || item.storeNo;
      });
      return this.mapToOptions(map);
    },
    areaOptions() {
      const map = {};
      this.flatStacks
        .filter((item) => !this.query.storeNo || item.storeNo === this.query.storeNo)
        .forEach((item) => {
          if (item.areaId) map[item.areaId] = item.areaName || item.areaId;
        });
      return this.mapToOptions(map);
    },
    locationOptions() {
      const map = {};
      this.flatStacks
        .filter((item) => !this.query.storeNo || item.storeNo === this.query.storeNo)
        .filter((item) => !this.query.areaId || String(item.areaId) === String(this.query.areaId))
        .forEach((item) => {
          if (item.locationCode) map[item.locationCode] = item.locationName || item.locationCode;
        });
      return this.mapToOptions(map);
    },
    varietyOptions() {
      const map = {};
      this.flatStacks.forEach((item) => {
        if (item.varNo) map[item.varNo] = item.varName || item.varNo;
      });
      return this.mapToOptions(map);
    },
    customerOptions() {
      const map = {};
      this.flatStacks.forEach((item) => {
        if (item.custNo) map[item.custNo] = item.custName || item.custNo;
      });
      return this.mapToOptions(map);
    },
    capacityMissingCount() {
      return this.flatStacks.filter((item) => this.toNumber(item.capacityAmount) <= 0).length;
    },
  },
  created() {
    this.fetchBoard();
  },
  methods: {
    fetchBoard() {
      this.loading = true;
      this.errorMessage = "";
      getYardSpaceBoard(this.cleanQuery())
        .then((res) => {
          const data = (res.data && res.data.data) || {};
          const payload = Array.isArray(data) ? { stacks: data } : data;
          this.boardData = {
            summary: payload.summary || {},
            varieties: payload.varieties || [],
            stores: payload.stores || [],
            stacks: payload.stacks || payload.records || payload.list || [],
          };
          this.selectedStack = this.flatStacks[0] || null;
        })
        .catch((error) => {
          this.errorMessage = (error && error.message) || "堆场空间看板数据加载失败";
          this.boardData = { summary: {}, varieties: [], stores: [], stacks: [] };
          this.selectedStack = null;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    cleanQuery() {
      const params = {};
      Object.keys(this.query).forEach((key) => {
        const value = this.query[key];
        if (value !== "" && value !== null && value !== undefined) params[key] = value;
      });
      return params;
    },
    resetQuery() {
      this.query = {
        storeNo: "",
        areaId: "",
        locationCode: "",
        varNo: "",
        custNo: "",
        stockStatus: "",
      };
      this.fetchBoard();
    },
    handleStoreChange() {
      this.query.areaId = "";
      this.query.locationCode = "";
    },
    handleAreaChange() {
      this.query.locationCode = "";
    },
    selectStack(stack) {
      this.selectedStack = stack;
    },
    normalizeArea(area, store, areaIndex) {
      const stacks = (area.stacks || []).map((item, index) => this.normalizeStack(item, store, area, index));
      const hasLayout = stacks.some((item) => this.hasLayout(item));
      return {
        ...area,
        areaKey: area.areaId || area.areaName || `area-${areaIndex}`,
        stacks,
        hasLayout,
        bounds: this.getAreaBounds(stacks),
      };
    },
    normalizeStack(item, store, area, index) {
      const capacityAmount = this.toNumber(item.capacityAmount);
      const stockAmount = this.toNumber(item.stockAmount);
      return {
        ...store,
        ...area,
        ...item,
        stackKey: item.id || item.locationCode || `${item.storeNo || store.storeNo || "store"}-${item.areaId || area.areaId || "area"}-${index}`,
        stockAmount,
        capacityAmount,
        capacityRate: this.resolveRate(stockAmount, capacityAmount, item.capacityRate),
      };
    },
    groupStacksToStores(stacks) {
      const stores = {};
      stacks.forEach((stack) => {
        const storeKey = stack.storeNo || "unknown-store";
        const areaKey = stack.areaId || stack.areaName || "default-area";
        if (!stores[storeKey]) {
          stores[storeKey] = {
            storeKey,
            storeNo: stack.storeNo,
            storeName: stack.storeName,
            areas: {},
          };
        }
        if (!stores[storeKey].areas[areaKey]) {
          stores[storeKey].areas[areaKey] = {
            areaKey,
            areaId: stack.areaId,
            areaName: stack.areaName,
            stacks: [],
          };
        }
        stores[storeKey].areas[areaKey].stacks.push(stack);
      });
      return Object.keys(stores).map((storeKey) => {
        const areas = Object.keys(stores[storeKey].areas).map((areaKey, index) => this.normalizeArea(stores[storeKey].areas[areaKey], stores[storeKey], index));
        const allStacks = areas.reduce((list, area) => list.concat(area.stacks), []);
        return {
          ...stores[storeKey],
          areas,
          stackCount: allStacks.length,
          stockAmount: allStacks.reduce((sum, item) => sum + this.toNumber(item.stockAmount), 0),
        };
      });
    },
    getAreaBounds(stacks) {
      const layoutStacks = stacks.filter((item) => this.hasLayout(item));
      if (!layoutStacks.length) return { minX: 0, minY: 0, width: 1, height: 1 };
      const xs = layoutStacks.map((item) => this.toNumber(item.layoutX));
      const ys = layoutStacks.map((item) => this.toNumber(item.layoutY));
      const rights = layoutStacks.map((item) => this.toNumber(item.layoutX) + Math.max(this.toNumber(item.layoutW), 1));
      const bottoms = layoutStacks.map((item) => this.toNumber(item.layoutY) + Math.max(this.toNumber(item.layoutH), 1));
      const minX = Math.min.apply(null, xs);
      const minY = Math.min.apply(null, ys);
      return {
        minX,
        minY,
        width: Math.max(Math.max.apply(null, rights) - minX, 1),
        height: Math.max(Math.max.apply(null, bottoms) - minY, 1),
      };
    },
    hasLayout(item) {
      return item.layoutX !== null && item.layoutX !== undefined && item.layoutY !== null && item.layoutY !== undefined;
    },
    stackListStyle(stack) {
      const height = 54 + this.stackVisualHeight(stack) * 0.36;
      return {
        minHeight: `${Math.max(58, Math.min(height, 90))}px`,
      };
    },
    stackStyle(stack, area) {
      if (!area.hasLayout || !this.hasLayout(stack)) {
        return {};
      }
      const bounds = area.bounds || this.getAreaBounds(area.stacks || []);
      const left = ((this.toNumber(stack.layoutX) - bounds.minX) / bounds.width) * 100;
      const top = ((this.toNumber(stack.layoutY) - bounds.minY) / bounds.height) * 100;
      const width = (Math.max(this.toNumber(stack.layoutW), 1) / bounds.width) * 100;
      const height = (Math.max(this.toNumber(stack.layoutH), 1) / bounds.height) * 100;
      return {
        left: `${Math.max(0, Math.min(left, 96))}%`,
        top: `${Math.max(0, Math.min(top, 92))}%`,
        width: `${Math.max(9, Math.min(width, 48))}%`,
        height: `${Math.max(54, Math.min(height, 120))}px`,
      };
    },
    stackVisualHeight(stack) {
      const value = (this.toNumber(stack.stockAmount) / this.maxStockAmount) * 100;
      return Math.max(8, Math.min(value, 100));
    },
    resolveRate(stockAmount, capacityAmount, rate) {
      if (rate !== null && rate !== undefined && rate !== "") return this.toNumber(rate);
      if (this.toNumber(capacityAmount) <= 0) return 0;
      return (this.toNumber(stockAmount) / this.toNumber(capacityAmount)) * 100;
    },
    formatLocations(locations) {
      const unique = Array.from(new Set((locations || []).filter(Boolean)));
      if (unique.length <= 3) return unique.join("、");
      return `${unique.slice(0, 3).join("、")} 等 ${unique.length} 处`;
    },
    hasMaintainedVariety(item) {
      const name = String((item && item.varName) || "").trim();
      return Boolean((item && item.varNo) || (name && name !== "未维护煤种"));
    },
    displayVarName(value) {
      const name = String(value || "").trim();
      return name && name !== "未维护煤种" ? name : "-";
    },
    mapToOptions(map) {
      return Object.keys(map).map((key) => ({ value: key, label: map[key] }));
    },
    statusClass(row) {
      if (row.stockStatus === 0 || this.toNumber(row.stockAmount) <= 0) return "stack-cell--empty";
      if (row.stockStatus === 2 || this.toNumber(row.capacityRate) >= 90) return "stack-cell--danger";
      return "stack-cell--normal";
    },
    statusType(status) {
      if (status === 0) return "info";
      if (status === 2) return "danger";
      return "success";
    },
    statusText(status) {
      if (status === 0) return "空仓";
      if (status === 2) return "高位";
      return "正常";
    },
    toPercent(value) {
      const num = this.toNumber(value);
      if (num < 0) return 0;
      if (num > 100) return 100;
      return Number(num.toFixed(2));
    },
    toNumber(value) {
      const num = Number(value || 0);
      return Number.isFinite(num) ? num : 0;
    },
    formatNumber(value) {
      return this.toNumber(value).toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },
  },
};
</script>

<style scoped lang="scss">
.yard-space-board {
  padding: 10px;
  background: #f5f7fa;

  .filter-panel,
  .yard-panel,
  .side-panel,
  .table-panel,
  .metric-card {
    background: #fff;
    border: 1px solid #ebeef5;
    border-radius: 6px;
    box-sizing: border-box;
  }

  .filter-panel {
    padding: 12px 14px 2px;
    margin-bottom: 10px;
  }

  .metric-grid {
    display: grid;
    grid-template-columns: repeat(7, minmax(126px, 1fr));
    gap: 10px;
    margin-bottom: 10px;
  }

  .metric-card {
    min-height: 62px;
    padding: 12px 14px;

    span {
      display: block;
      color: #64748b;
      font-size: 13px;
      line-height: 18px;
    }

    strong {
      display: block;
      margin-top: 8px;
      color: #111827;
      font-size: 21px;
      line-height: 24px;
      font-weight: 700;
    }

    &--primary strong {
      color: #2563eb;
    }

    &--success strong {
      color: #15803d;
    }

    &--warning strong {
      color: #b45309;
    }

    &--danger strong {
      color: #dc2626;
    }

    &--time strong {
      font-size: 15px;
      line-height: 20px;
    }
  }

  .board-alert {
    margin-bottom: 10px;
  }

  .board-layout {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 360px;
    gap: 10px;
    min-height: 420px;
  }

  .yard-panel,
  .side-panel,
  .table-panel {
    padding: 12px;
  }

  .table-panel {
    margin-top: 10px;
  }

  .panel-title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 28px;
    margin-bottom: 10px;

    span {
      color: #303133;
      font-size: 15px;
      font-weight: 600;
    }

    em {
      color: #909399;
      font-size: 12px;
      font-style: normal;
    }
  }

  .store-block + .store-block {
    margin-top: 12px;
  }

  .store-title,
  .area-title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    color: #303133;
  }

  .store-title {
    min-height: 34px;
    padding: 0 4px;

    span {
      color: #606266;
      font-size: 12px;
    }
  }

  .area-block {
    margin-top: 8px;
  }

  .area-title {
    min-height: 26px;
    padding: 0 2px;

    span {
      font-size: 13px;
      font-weight: 600;
    }

    em {
      color: #909399;
      font-size: 12px;
      font-style: normal;
    }
  }

  .yard-map {
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-height: 420px;
    min-height: 120px;
    padding: 8px;
    background: #f8fafc;
    border: 1px solid #dfe6ef;
    border-radius: 4px;
    overflow-y: auto;
    overflow-x: hidden;
  }

  .yard-map--free {
    min-height: 120px;
  }

  .stack-cell {
    position: relative;
    flex: 0 0 auto;
    width: 100%;
    padding: 0;
    color: #303133;
    background: #fff;
    border: 1px solid #cfd8e3;
    border-radius: 4px;
    box-shadow: none;
    cursor: pointer;
    overflow: hidden;
    text-align: left;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
  }

  .stack-cell:hover,
  .stack-cell--active {
    border-color: #409eff;
    box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.12);
  }

  .stack-cell__fill {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(64, 158, 255, 0.18);
  }

  .stack-cell--empty .stack-cell__fill {
    background: rgba(144, 147, 153, 0.16);
  }

  .stack-cell--normal .stack-cell__fill {
    background: rgba(103, 194, 58, 0.18);
  }

  .stack-cell--danger .stack-cell__fill {
    background: rgba(245, 108, 108, 0.2);
  }

  .stack-cell__content {
    position: relative;
    z-index: 1;
    display: grid;
    grid-template-rows: 18px 18px;
    gap: 3px;
    padding: 7px 8px;
    font-size: 12px;
    min-height: 50px;
    box-sizing: border-box;

    strong {
      color: #303133;
      font-size: 13px;
      line-height: 18px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    em {
      min-width: 0;
      color: #606266;
      font-size: 12px;
      font-style: normal;
      line-height: 16px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    small {
      flex: 0 0 auto;
      color: #606266;
      font-size: 12px;
      line-height: 16px;
      white-space: nowrap;
    }
  }

  .stack-cell__meta {
    display: flex;
    min-width: 0;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  .side-panel {
    align-self: start;
  }

  .panel-section + .panel-section {
    margin-top: 14px;
    padding-top: 14px;
    border-top: 1px solid #ebeef5;
  }

  .variety-list {
    display: grid;
    gap: 10px;
    max-height: 310px;
    overflow: auto;
  }

  .variety-item {
    padding: 10px;
    background: #f8fafc;
    border: 1px solid #edf2f7;
    border-radius: 4px;
  }

  .variety-item__head,
  .variety-item__foot {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  .variety-item__head {
    margin-bottom: 8px;

    strong {
      color: #303133;
      font-size: 13px;
    }

    span {
      color: #2563eb;
      font-size: 13px;
      font-weight: 600;
    }
  }

  .variety-item__foot {
    margin-top: 7px;
    color: #909399;
    font-size: 12px;

    em {
      max-width: 180px;
      color: #606266;
      font-style: normal;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .detail-section {
    ::v-deep .el-descriptions-item__label {
      width: 92px;
      color: #606266;
    }
  }

  .capacity-cell {
    display: grid;
    grid-template-columns: 1fr 46px;
    align-items: center;
    gap: 8px;
  }
}

::v-deep .el-form-item {
  margin-bottom: 10px;
}

::v-deep .el-select {
  width: 168px;
}

@media (max-width: 1500px) {
  .yard-space-board {
    .metric-grid {
      grid-template-columns: repeat(4, minmax(0, 1fr));
    }

    .board-layout {
      grid-template-columns: minmax(0, 1fr);
    }
  }
}

@media (max-width: 768px) {
  .yard-space-board {
    .metric-grid {
      grid-template-columns: repeat(1, minmax(0, 1fr));
    }

    .yard-map--free {
      grid-template-columns: repeat(1, minmax(0, 1fr));
    }
  }
}
</style>
